import { Fragment, useCallback, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useReadQuery } from "@apollo/client";
import {
  Body,
  LoadingSplashOverlayProvider,
} from "@songtradr-one/components-frontend";
import { HashedDynamicPermission } from "@songtradr-one/shared-common";
import { HeapAnalytics } from "../../components/analytics/HeapAnalytics";
import { Flex } from "../../components/flex/Flex.Parts";
import { useAppContext } from "../../context/app-context/AppContext";
import { useModalConfirm } from "../../context/modal-confirm-context/ModalConfirmContext";
import { IsImFrame, Props } from "./User.Parts";

export const User = ({
  queryRef,
  header,
  footer,
  rebrand = false,
  guard: Guard = Fragment,
}: Props) => {
  const { setUser, setCurrentOrg } = useAppContext();
  const { confirm } = useModalConfirm();
  const { data } = useReadQuery(queryRef);
  const isImInfo = data?.user?.isImInfo;
  const [showIsImFrame, setShowIsImFrame] = useState(isImInfo?.isIm || false);

  useEffect(() => {
    if (data && data.user && data.org) {
      const hasLabAppAccess =
        Array.isArray(data.user.labApps) && data.user.labApps.length > 0;
      setUser(
        hasLabAppAccess
          ? {
              ...data.user,
              permissions: [
                ...data.user.permissions,
                HashedDynamicPermission.LABS_APP_ACCESS,
              ],
            }
          : data.user,
      );
      setCurrentOrg(data.org, data.user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Allow hiding the isIm frame with Ctrl+Shift+F12 to allow for a screen recording without the frame
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.ctrlKey && e.shiftKey && e.key === "F12") {
      setShowIsImFrame((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    if (isImInfo?.isIm) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isImInfo?.isIm, handleKeyDown]);

  // Show isIm warning message on initial load
  useEffect(() => {
    if (isImInfo?.isIm) {
      confirm({
        title: isImInfo.title,
        content: (
          <Flex $direction="column" $gap="1rem">
            <Body>{isImInfo.body}</Body>
            <Body>{isImInfo.disclaimer}</Body>
          </Flex>
        ),
        confirmLabel: isImInfo.confirmLabel,
        preventDismiss: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (rebrand)
    return (
      <LoadingSplashOverlayProvider>
        {showIsImFrame && <IsImFrame />}
        <Guard>
          <Outlet />
        </Guard>
      </LoadingSplashOverlayProvider>
    );
  else
    return (
      <LoadingSplashOverlayProvider>
        {showIsImFrame && <IsImFrame />}
        <HeapAnalytics />
        <Flex $flow="column" $gap={0} $minHeight="100%" $grow={1}>
          {header}
          <Guard>
            <Outlet />
          </Guard>
          {footer}
        </Flex>
      </LoadingSplashOverlayProvider>
    );
};
